import { useEffect, useCallback, useRef } from "react"

import useAdBlock from "utils/useAdBlock"
import highValueCommercePages from "content/high-value-commerce-pages.json"

const useAds = (article, isPreview, htlScriptLoaded) => {
  const { elements, internalTags, useLeaderboard, template, url } = article

  const isPremium = template === "premium"
  const isStandard = template === "standard"

  const showAds = !isPreview && !highValueCommercePages.articles.includes(url)

  const adBlockDetected = useAdBlock()
  const hasProduct = elements?.some(({ type }) => type === "product")

  const inlineAdsRendered = useRef(false)
  const rightRailAdsRendered = useRef(false)
  const topAdRendered = useRef(false)
  const leaderboardAdRendered = useRef(false)
  const bottomStickyAdRendered = useRef(false)

  const showInlineAndRightRailAds = useCallback(() => {
    if (showAds && !adBlockDetected) {
      const maxIterations = 15

      if (!inlineAdsRendered.current) {
        let i = 0
        const intervalId1 = setInterval(() => {
          const ads = [
            ...Array.from(document.querySelectorAll(".inline-ad")),
            ...Array.from(document.querySelectorAll(".inline-contentful-ad")),
          ]
          ads.forEach((ad) => {
            ad.style.visibility = "visible"
          })
          i += 1
          if (ads.length > 0 || i === maxIterations) {
            clearInterval(intervalId1)
          }
        }, 150)
      }

      if (!rightRailAdsRendered.current) {
        let j = 0
        const intervalId2 = setInterval(() => {
          const ads = [
            ...Array.from(document.querySelectorAll(".right-rail-ad")),
            ...Array.from(document.querySelectorAll(".right-rail-contentful-ad")),
          ]
          ads.forEach((ad) => {
            ad.style.visibility = "visible"
          })
          j += 1
          if (ads.length > 0 || j === maxIterations) {
            clearInterval(intervalId2)
          }
        }, 150)
      }
    }
  }, [showAds, adBlockDetected])

  const hideAllAds = useCallback(() => {
    if (!showAds || isPremium || adBlockDetected) {
      setTimeout(() => {
        const leaderboardAd = document.querySelector("#article-leaderboard-ad")
        const topAd = document.querySelector("#article-top-ad")
        const bottomStickyAd = document.querySelector("#article-bottom-sticky-ad")

        if (leaderboardAd) leaderboardAd.style.display = "none"
        if (topAd) topAd.style.display = "none"
        if (bottomStickyAd) bottomStickyAd.style.display = "none"

        const networkAds = Array.from(document.querySelectorAll(".network-ad"))
        networkAds.forEach((ad) => {
          if (ad && ad.parentNode) ad.parentNode.style.display = "none"
        })

        const contentfulAds = [
          ...Array.from(document.querySelectorAll(".inline-contentful-ad")),
          ...Array.from(document.querySelectorAll(".right-rail-contentful-ad")),
        ]
        contentfulAds.forEach((ad) => {
          ad.style.display = "none"
        })
      }, 500)
    }
  }, [showAds, isPremium, adBlockDetected])

  useEffect(() => {
    const isSearch = Boolean(document.referrer?.match(/^https:\/\/www\.(google|bing)\.com/))
    const hasLeaderboardAd = isStandard && (useLeaderboard || !isSearch)
    const hasFastAds = internalTags?.includes("fast ads")
    if (htlScriptLoaded) {
      const leaderboardAd = document.querySelector("#article-leaderboard-ad")
      if (hasLeaderboardAd || hasFastAds) {
        // if we are using a leaderboard ad or if internal tags includes "fast ads" value
        // show inline & right rail ads right away
        showInlineAndRightRailAds()

        if (!leaderboardAdRendered.current) {
          leaderboardAd.style.visibility = "visible"
          leaderboardAdRendered.current = true
        }
      } else if (leaderboardAd) {
        leaderboardAd.style.display = "none"
      }

      // also show top ad (mobile) if we are using fast ads
      const hasTopAd = !isPremium
      const topAd = document.querySelector("#article-top-ad")
      if (hasTopAd || hasFastAds) {
        if (!topAdRendered.current) {
          topAd.style.visibility = "visible"
          topAdRendered.current = true
        }
      } else if (topAd) {
        topAd.style.display = "none"
      }
    }

    hideAllAds()

    const handleScroll = (event) => {
      const currentScrollTop = event.target.documentElement.scrollTop
      if (currentScrollTop > 0) {
        // otherwise, show inline & right rail ads on first scroll
        showInlineAndRightRailAds()
        inlineAdsRendered.current = true
        rightRailAdsRendered.current = true

        const hasTopAd = !isPremium
        const topAd = document.querySelector("#article-top-ad")
        if (hasTopAd) {
          if (!topAdRendered.current) {
            topAd.style.visibility = "visible"
            topAdRendered.current = true
          }
        } else if (topAd) {
          topAd.style.display = "none"
        }

        const hasBottomStickyAd = isStandard && !hasLeaderboardAd && !hasProduct
        const bottomStickyAd = document.querySelector("#article-bottom-sticky-ad")
        if (hasBottomStickyAd) {
          if (!bottomStickyAdRendered.current) {
            bottomStickyAd.style.visibility = "visible"
            bottomStickyAdRendered.current = true
          }
        } else if (bottomStickyAd) {
          bottomStickyAd.style.display = "none"
        }
      }

      hideAllAds()
    }

    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [
    htlScriptLoaded,
    isPremium,
    isStandard,
    useLeaderboard,
    hasProduct,
    showInlineAndRightRailAds,
    hideAllAds,
  ])

  return {
    showAds,
  }
}

export default useAds
